import React from 'react';
import { AlertCircle, Check, ChevronDown } from 'lucide-react';

/**
 * Renderiza um campo do formulário do Processo Seletivo a partir da
 * definição declarada em src/data/processoSeletivoData.js.
 *
 * Tipos suportados:
 * text | textarea | email | tel | number | url | radio | select | checkbox | consent
 */

// Normaliza options: aceita ['A', 'B'] ou [{ value, label }]
export const normalizeOptions = (options = []) =>
  options.map((opt) =>
    typeof opt === 'string' ? { value: opt, label: opt } : opt
  );

const baseInput =
  'w-full bg-night-900/70 border rounded-2xl px-5 py-4 text-mist-50 placeholder:text-mist-600 ' +
  'outline-none transition-all duration-300 focus:bg-night-900 ' +
  'focus:ring-4 focus:ring-primary-400/10';

const FormField = ({ question, value, error, onChange }) => {
  const { id, label, type, required, placeholder, help, maxLength, min, max } = question;

  const borderClass = error
    ? 'border-red-500/60 focus:border-red-400'
    : 'border-white/[0.09] hover:border-white/[0.18] focus:border-primary-400/70';

  const describedBy = [error ? `${id}-error` : null, help ? `${id}-help` : null]
    .filter(Boolean)
    .join(' ') || undefined;

  const renderControl = () => {
    switch (type) {
      /* ---------------------------------------------------- resposta longa */
      case 'textarea':
        return (
          <textarea
            id={id}
            name={id}
            rows={5}
            value={value || ''}
            maxLength={maxLength}
            placeholder={placeholder}
            aria-invalid={!!error}
            aria-describedby={describedBy}
            onChange={(e) => onChange(id, e.target.value)}
            className={`${baseInput} ${borderClass} resize-y min-h-[140px] leading-relaxed`}
          />
        );

      /* -------------------------------------------------- marcar uma opção */
      case 'radio': {
        const options = normalizeOptions(question.options);
        return (
          <div role="radiogroup" aria-describedby={describedBy} className="grid gap-2.5">
            {options.map((opt) => {
              const checked = value === opt.value;
              return (
                <label
                  key={opt.value}
                  className={
                    'group relative flex items-center gap-4 cursor-pointer rounded-2xl border px-5 py-4 transition-all duration-300 ' +
                    (checked
                      ? 'bg-primary-400/[0.09] border-primary-400/60 shadow-[0_0_0_4px_rgba(74,222,128,0.07)]'
                      : 'bg-night-900/70 border-white/[0.09] hover:border-white/[0.2] hover:bg-night-900')
                  }
                >
                  <input
                    type="radio"
                    name={id}
                    value={opt.value}
                    checked={checked}
                    onChange={() => onChange(id, opt.value)}
                    className="sr-only"
                  />
                  <span
                    className={
                      'shrink-0 h-5 w-5 rounded-full border-2 flex items-center justify-center transition-all duration-300 ' +
                      (checked
                        ? 'border-primary-400 bg-primary-400'
                        : 'border-white/25 group-hover:border-white/45')
                    }
                  >
                    {checked && <Check size={12} strokeWidth={3.5} className="text-night-950" />}
                  </span>
                  <span
                    className={
                      'text-[0.95rem] leading-snug transition-colors ' +
                      (checked ? 'text-mist-50 font-medium' : 'text-mist-300')
                    }
                  >
                    {opt.label}
                  </span>
                </label>
              );
            })}
          </div>
        );
      }

      /* ------------------------------------------------- marcar várias opções */
      case 'checkbox': {
        const options = normalizeOptions(question.options);
        const selected = Array.isArray(value) ? value : [];
        const columns = question.columns || (options.length > 8 ? 3 : options.length > 4 ? 2 : 1);
        const gridClass =
          columns >= 3
            ? 'sm:grid-cols-2 lg:grid-cols-3'
            : columns === 2
            ? 'sm:grid-cols-2'
            : '';

        const toggle = (optValue) => {
          const next = selected.includes(optValue)
            ? selected.filter((v) => v !== optValue)
            : [...selected, optValue];
          onChange(id, next);
        };

        return (
          <div aria-describedby={describedBy} className={`grid gap-2.5 ${gridClass}`}>
            {options.map((opt) => {
              const checked = selected.includes(opt.value);
              return (
                <label
                  key={opt.value}
                  className={
                    'group relative flex items-center gap-3 cursor-pointer rounded-2xl border px-4 py-3.5 transition-all duration-300 ' +
                    (checked
                      ? 'bg-primary-400/[0.09] border-primary-400/60 shadow-[0_0_0_4px_rgba(74,222,128,0.07)]'
                      : 'bg-night-900/70 border-white/[0.09] hover:border-white/[0.2] hover:bg-night-900')
                  }
                >
                  <input
                    type="checkbox"
                    name={id}
                    value={opt.value}
                    checked={checked}
                    onChange={() => toggle(opt.value)}
                    className="sr-only"
                  />
                  <span
                    className={
                      'shrink-0 h-5 w-5 rounded-md border-2 flex items-center justify-center transition-all duration-300 ' +
                      (checked
                        ? 'border-primary-400 bg-primary-400'
                        : 'border-white/25 group-hover:border-white/45')
                    }
                  >
                    {checked && <Check size={12} strokeWidth={3.5} className="text-night-950" />}
                  </span>
                  <span
                    className={
                      'text-[0.9rem] leading-snug transition-colors ' +
                      (checked ? 'text-mist-50 font-medium' : 'text-mist-300')
                    }
                  >
                    {opt.label}
                  </span>
                </label>
              );
            })}
          </div>
        );
      }

      /* -------------------------------------------- caixa única de confirmação */
      case 'consent': {
        const checked = value === true;
        return (
          <label
            className={
              'group flex items-start gap-4 cursor-pointer rounded-2xl border px-5 py-5 transition-all duration-300 ' +
              (checked
                ? 'bg-primary-400/[0.09] border-primary-400/60'
                : error
                ? 'bg-night-900/70 border-red-500/60'
                : 'bg-night-900/70 border-white/[0.09] hover:border-white/[0.2]')
            }
          >
            <input
              type="checkbox"
              id={id}
              name={id}
              checked={checked}
              aria-describedby={describedBy}
              onChange={(e) => onChange(id, e.target.checked)}
              className="sr-only"
            />
            <span
              className={
                'shrink-0 mt-0.5 h-5 w-5 rounded-md border-2 flex items-center justify-center transition-all duration-300 ' +
                (checked
                  ? 'border-primary-400 bg-primary-400'
                  : 'border-white/25 group-hover:border-white/45')
              }
            >
              {checked && <Check size={12} strokeWidth={3.5} className="text-night-950" />}
            </span>
            <span
              className={
                'text-[0.9rem] leading-relaxed transition-colors ' +
                (checked ? 'text-mist-100' : 'text-mist-300')
              }
            >
              {label}
            </span>
          </label>
        );
      }

      /* ------------------------------------------------------- menu suspenso */
      case 'select': {
        const options = normalizeOptions(question.options);
        return (
          <div className="relative">
            <select
              id={id}
              name={id}
              value={value || ''}
              aria-invalid={!!error}
              aria-describedby={describedBy}
              onChange={(e) => onChange(id, e.target.value)}
              className={`${baseInput} ${borderClass} appearance-none pr-12 cursor-pointer ${
                value ? 'text-mist-50' : 'text-mist-600'
              }`}
            >
              <option value="" disabled>
                {placeholder || 'Selecione uma opção'}
              </option>
              {options.map((opt) => (
                <option key={opt.value} value={opt.value} className="bg-night-800 text-mist-50">
                  {opt.label}
                </option>
              ))}
            </select>
            <ChevronDown
              size={18}
              className="pointer-events-none absolute right-5 top-1/2 -translate-y-1/2 text-mist-500"
            />
          </div>
        );
      }

      /* ------------------------------------------ demais campos de uma linha */
      default:
        return (
          <input
            id={id}
            name={id}
            type={type === 'url' ? 'text' : type}
            inputMode={type === 'number' ? 'numeric' : type === 'tel' ? 'tel' : undefined}
            value={value || ''}
            maxLength={maxLength}
            min={min}
            max={max}
            placeholder={placeholder}
            aria-invalid={!!error}
            aria-describedby={describedBy}
            onChange={(e) => onChange(id, e.target.value)}
            className={`${baseInput} ${borderClass}`}
          />
        );
    }
  };

  const showCounter = maxLength && (type === 'textarea' || type === 'text');
  const wrapperClass = question.width === 'half' ? 'md:col-span-1' : 'md:col-span-2';

  // O 'consent' já mostra o texto dentro da própria caixa
  if (type === 'consent') {
    return (
      <div className={wrapperClass}>
        {renderControl()}
        {error && (
          <p
            id={`${id}-error`}
            role="alert"
            className="mt-2 flex items-center gap-1.5 text-xs text-red-400"
          >
            <AlertCircle size={13} className="shrink-0" />
            {error}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className={wrapperClass}>
      <div className="flex items-baseline justify-between gap-4 mb-2.5">
        <label
          htmlFor={type === 'radio' || type === 'checkbox' ? undefined : id}
          className="block text-sm font-medium text-mist-100 leading-snug"
        >
          {label}
          {required ? (
            <span className="text-primary-400 ml-1" aria-hidden="true">*</span>
          ) : (
            <span className="ml-2 text-[0.7rem] uppercase tracking-wider text-mist-600 font-normal">
              opcional
            </span>
          )}
        </label>
        {showCounter && (
          <span
            className={
              'shrink-0 text-[0.7rem] tabular-nums ' +
              ((value || '').length > maxLength * 0.9 ? 'text-primary-400' : 'text-mist-600')
            }
          >
            {(value || '').length}/{maxLength}
          </span>
        )}
      </div>

      {renderControl()}

      {help && !error && (
        <p id={`${id}-help`} className="mt-2 text-xs text-mist-500 leading-relaxed">
          {help}
        </p>
      )}

      {error && (
        <p
          id={`${id}-error`}
          role="alert"
          className="mt-2 flex items-center gap-1.5 text-xs text-red-400"
        >
          <AlertCircle size={13} className="shrink-0" />
          {error}
        </p>
      )}
    </div>
  );
};

export default FormField;
